<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? "Editar concepto" : "Nuevo concepto" }}
      </h1>
    </div>

    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>
        <b-card tag="article" no-body style="max-width: 700px" class="custom-card">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>{{ editing ? "Datos del concepto" : "Datos del nuevo concepto" }}</h3>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <section class="form-section mb-0">
              <div class="row">

                <!-- Código -->
                <div class="col-md-3">
                  <div class="form-group">
                    <label for="code">Código <span class="text-danger">*</span></label>
                    <input
                      id="code"
                      type="text"
                      v-model="form.code"
                      autocomplete="off"
                      placeholder="Ej: A001"
                      class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.code.$error }"
                    />
                    <FormError
                      v-if="$v.form.code.$error && !$v.form.code.required"
                      message="El código es requerido"
                    />
                  </div>
                </div>

                <!-- Nombre -->
                <div class="col-md-9">
                  <div class="form-group">
                    <label for="name">Nombre <span class="text-danger">*</span></label>
                    <input
                      id="name"
                      type="text"
                      v-model="form.name"
                      autocomplete="off"
                      placeholder="Nombre del concepto"
                      class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.name.$error }"
                    />
                    <FormError
                      v-if="$v.form.name.$error && !$v.form.name.required"
                      message="El nombre es requerido"
                    />
                  </div>
                </div>

                <!-- Tipo -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Tipo <span class="text-danger">*</span></label>
                    <FormSelect
                      :items="typeOptions"
                      v-model="form.type"
                      defaultOption="Seleccione tipo"
                      :disabled="editing"
                      :showError="$v.form.type.$error && !$v.form.type.required"
                    />
                    <FormError
                      v-if="$v.form.type.$error && !$v.form.type.required"
                      message="El tipo es requerido"
                    />
                  </div>
                </div>

                <!-- Naturaleza -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Naturaleza <span class="text-danger">*</span></label>
                    <FormSelect
                      :items="natureOptions"
                      v-model="form.nature"
                      defaultOption="Seleccione naturaleza"
                      :showError="$v.form.nature.$error && !$v.form.nature.required"
                    />
                    <FormError
                      v-if="$v.form.nature.$error && !$v.form.nature.required"
                      message="La naturaleza es requerida"
                    />
                  </div>
                </div>

                <!-- Afecta IR 5ta categoría -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Afecta IR 5ta categoría</label>
                    <div class="mt-2">
                      <b-form-checkbox
                        v-model="form.affectsFifthCategory"
                        switch
                        size="lg"
                      >
                        {{ form.affectsFifthCategory ? 'Sí' : 'No' }}
                      </b-form-checkbox>
                    </div>
                  </div>
                </div>

                <!-- Descripción -->
                <div class="col-md-12">
                  <div class="form-group">
                    <label for="description">Descripción</label>
                    <textarea
                      id="description"
                      v-model="form.description"
                      rows="3"
                      placeholder="Descripción opcional del concepto"
                      class="custom-form-control"
                    ></textarea>
                  </div>
                </div>

              </div>

              <div class="form-group-buttons text-right">
                <router-link :to="{ name: 'payroll-concept-list' }" class="button button-light">
                  {{ editing ? "Volver" : "Cancelar" }}
                </router-link>
                <button class="button button-primary" type="submit">Guardar</button>
              </div>
            </section>
          </b-card-body>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormError, FormSelect } from "wize-admin";
import { required } from "vuelidate/lib/validators";
import { PayrollConceptService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

export default {
  components: { FormError, FormSelect },

  data() {
    return {
      editing: false,
      typeOptions: Object.values(LocalConstants.PayrollConceptType).map((t) => ({
        key: t.key, label: t.label,
      })),
      natureOptions: Object.values(LocalConstants.PayrollConceptNature).map((n) => ({
        key: n.key, label: n.label,
      })),
      form: {
        code:                 null,
        name:                 null,
        type:                 null,
        nature:               null,
        affectsFifthCategory: false,
        description:          null,
      },
    };
  },

  validations: {
    form: {
      code:   { required },
      name:   { required },
      type:   { required },
      nature: { required },
    },
  },

  watch: {
    "$route.params.id": function () {
      if (this.$route.params.id) this.load(this.$route.params.id);
    },
  },

  methods: {
    async save() {
      this.$v.$touch();
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({
            scrollTo: document.querySelector(".form-error"),
            updateHistory: false,
            offset: -50,
          });
        });
        return;
      }

      try {
        this.$store.dispatch("app/loading", true);
        const data = {
          code:                 this.form.code,
          name:                 this.form.name,
          type:                 this.form.type,
          nature:               this.form.nature,
          affectsFifthCategory: this.form.affectsFifthCategory || false,
          description:          this.form.description,
        };

        let response;
        if (!this.editing) response = await PayrollConceptService.save(data);
        else response = await PayrollConceptService.update(this.$route.params.id, data);

        Alert.success("Se almacenó la información correctamente");
        if (!this.editing) {
          this.$router.push({
            name: "payroll-concept-edit",
            params: { id: response.payload.id },
          });
          this.editing = true;
        }
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "Error al guardar");
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },

    async load(id) {
      try {
        this.$store.dispatch("app/loading", true);
        const payload = (await PayrollConceptService.get(id)).payload;
        this.form = {
          code:                 payload.code,
          name:                 payload.name,
          type:                 payload.type,
          nature:               payload.nature,
          affectsFifthCategory: payload.affectsFifthCategory || false,
          description:          payload.description,
        };
        this.editing = true;
      } catch (e) {
        console.error(e);
        Alert.error(e.errors ? e.errors.message : "Error al cargar el concepto");
        this.$router.push({ name: "payroll-concept-list" });
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
  },

  async created() {
    if (this.$route.params.id) this.load(this.$route.params.id);
  },
};
</script>

<style lang="stylus" scoped></style>
